import {
  useShowVpnTools,
  useKeycloakAuth,
} from "../business-components/adminSettings/AdminContext";
import { isAppTypeIframe } from "../getAppType";
import { CvpSettings } from "../settings/CvpSettings";
import { useSettings } from "../settings/useSettings";
import { getRedirectProps, IGetRedirectPropsResponse } from "./redirect";
import { useCurrentPath } from "./useCurrentPath";
import { CVPSearchParams } from "./wsQueryStringStore";

// This is used to redirect the user to the correct page after a attach-profile token has been sent

export interface UseLoginPropsArgs {
  /** Default is currentPath */
  hasProfileRedirectUrl?: string;

  /** Default is /profile */
  noProfileRedirectUrl?: string;
}

export function useLoginProps(args: UseLoginPropsArgs = {}): LoginProps {
  const settings = useSettings();
  const currentPath = useCurrentPath();
  const showVpnTools = useShowVpnTools();
  const useKeycloak = useKeycloakAuth();

  return getLoginProps(currentPath, settings, args, showVpnTools, useKeycloak);
}

// This is used to redirect the user to the correct page after a attach-profile token has been sent
export function getLoginProps(
  currentPath: string,
  settings: CvpSettings,
  args: UseLoginPropsArgs,
  showVpnTools: boolean,
  useKeycloak: boolean
): LoginProps {
  let { hasProfileRedirectUrl, noProfileRedirectUrl } = getUrlsToGoToAfterLogin(
    currentPath,
    args
  );

  if (isAppTypeIframe() && settings.iframe_loginOldPortal) {
    return {
      redirectProps: {
        href: `https://secure.webtemp.no/WebtempCVPortal/default.aspx?k=${window.cvpIdConcern}`,
        target: "_blank",
      },
    };
  }

  const searchParams = new URLSearchParams();
  searchParams.set("redirectUri", hasProfileRedirectUrl);
  searchParams.set("noProfileRedirectUri", noProfileRedirectUrl);

  if (window.cvpIdConcern) {
    searchParams.set("vc_concernid", window.cvpIdConcern.toString());
    searchParams.set("k", window.cvpIdConcern.toString());
  }
  if (window.cvpEmbedType) {
    searchParams.set("vc_embedtype", window.cvpEmbedType);
  }
  if (showVpnTools) {
    searchParams.set("enableParameterOverride", "true");
  }

  searchParams.set("vc_primarycolor", settings.lt_primaryColor);

  const qs = searchParams.toString();

  // Use Keycloak only when toggle is enabled (for local IP users)
  // Otherwise use VismaConnect
  const authProvider = useKeycloak ? "keycloak" : "visma-connect";

  return {
    redirectProps: getRedirectProps(
      `${location.origin}/auth/${authProvider}/login?${qs}`,
      {
        mergeQs: {},
        redirectRaw: true,
      }
    ),
  };
}

function getUrlsToGoToAfterLogin(currentPath: string, args: UseLoginPropsArgs) {
  let {
    hasProfileRedirectUrl = currentPath,
    noProfileRedirectUrl = "/profile",
  } = args;

  hasProfileRedirectUrl = urlCorrections(hasProfileRedirectUrl);
  noProfileRedirectUrl = urlCorrections(noProfileRedirectUrl);

  const _hasProfileRedirectUrl = getRedirectProps(hasProfileRedirectUrl, {
    mergeQs: {
      k: window.cvpIdConcern ?? null,
      [CVPSearchParams.cvprb]: "true",
    },
  }).href;

  const _noProfileRedirectUrl = getRedirectProps(noProfileRedirectUrl, {
    mergeQs: {
      k: window.cvpIdConcern ?? null,
      [CVPSearchParams.cvprb]: "true",
    },
  }).href;

  return {
    hasProfileRedirectUrl: _hasProfileRedirectUrl,
    noProfileRedirectUrl: _noProfileRedirectUrl,
  };
}

function urlCorrections(url: string): string {
  // add leading slash if missing
  if (!url.startsWith("/")) {
    url = `/${url}`;
  }

  if (url === "/login") {
    // dont wanna go to this page after login
    url = "/";
  }

  if (url === "/applicationsent") {
    // dont wanna go to this page after login
    url = "/profile";
  }

  return url;
}

export interface LoginProps {
  redirectProps: IGetRedirectPropsResponse;
}
